/**
 * Rachawk Cart Drawer – AJAX Cart + Wishlist System
 * 
 * Multi-state drawer:
 *   STATE 1: "Added to Cart" confirmation + coupon
 *   STATE 2: Full cart view + recommendations
 *   STATE 3: Wishlist (localStorage + size selector + move to cart)
 *   STATE 4: Remove confirmation modal
 *
 * All cart operations are AJAX — zero page reloads.
 */

(function () {
  'use strict';

  // Warm Shopify/Cloudflare bot-verification BEFORE the user can click Buy Now.
  // Cloudflare only challenges write (POST) requests, so a GET warm-up isn't
  // enough — we do a no-op POST to /cart/update.js so the challenge resolves
  // and the verification cookie is set before Razorpay's first /cart/add.js.
  var botCheckWarmUp = Promise.resolve();
  try {
    botCheckWarmUp = fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: {} })
    }).catch(function () {});
  } catch (e) {}

  /* ============================================
     SELECTORS
     ============================================ */

  const drawer = document.getElementById('cart-drawer');
  const overlay = document.getElementById('cart-drawer-overlay');
  const drawerModalOverlay = document.getElementById('cart-drawer-modal-overlay');

  // States
  const stateAdded = document.getElementById('drawer-state-added');
  const stateCart = document.getElementById('drawer-state-cart');
  const stateWishlist = document.getElementById('drawer-state-wishlist');

  // State 1 elements
  const addedItemContainer = document.getElementById('added-item-container');
  const couponBox = document.getElementById('coupon-box');
  const couponTitle = document.getElementById('coupon-title');
  const couponCodeValue = document.getElementById('coupon-code-value');
  const couponGrabBtn = document.getElementById('coupon-grab-btn');
  const viewCartBtn = document.getElementById('view-cart-btn');
  const quickCheckoutBtn = document.getElementById('quick-checkout-btn');
  const quickCheckoutTotal = document.getElementById('quick-checkout-total');

  // State 2 elements
  const cartItemsList = document.getElementById('cart-items-list');
  const cartEmpty = document.getElementById('cart-empty');
  const cartLoading = document.getElementById('cart-loading');
  const cartRecs = document.getElementById('cart-recs');
  const cartRecsGrid = document.getElementById('cart-recs-grid');
  const cartFooter = document.getElementById('cart-footer');
  const cartCheckoutBtn = document.getElementById('cart-checkout-btn');
  const cartCheckoutTotal = document.getElementById('cart-checkout-total');

  // State 3 elements
  const wishlistItemsList = document.getElementById('wishlist-items-list');
  const wishlistEmpty = document.getElementById('wishlist-empty-user');

  // Customer data
  let customerData = { loggedIn: false, moneyFormat: '₹{{amount}}' };
  try {
    const el = document.getElementById('cart-drawer-customer-data');
    if (el) customerData = JSON.parse(el.textContent);
  } catch (e) { /* noop */ }

  /* ============================================
     STATE MANAGEMENT
     ============================================ */

  let currentState = null;
  let pendingRemove = null; // { key, handle, title, source: 'cart' | 'wishlist' }
  let lastAddedProductData = null;
  let productVariantsCache = {}; // Cache for product variants

  function switchState(state) {
    currentState = state;
    [stateAdded, stateCart, stateWishlist].forEach(function (s) {
      if (s) s.classList.remove('is-active');
    });
    const target = document.getElementById('drawer-state-' + state);
    if (target) target.classList.add('is-active');
  }

  function openDrawer(state) {
    switchState(state);
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    setTimeout(function () {
      const closeBtn = drawer.querySelector('[data-drawer-close]');
      if (closeBtn) closeBtn.focus();
    }, 100);
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    currentState = null;
  }

  function openRemoveModal(data) {
    pendingRemove = data;
    
    var modalTitle = document.getElementById('modal-title');
    var modalSub = document.getElementById('modal-sub');
    var modalActions = document.getElementById('modal-actions');
    
    // Update modal content based on source
    if (data.source === 'wishlist') {
      // Simple confirmation for wishlist
      if (modalTitle) modalTitle.textContent = 'Are you sure?';
      if (modalSub) modalSub.innerHTML = 'This item will be removed from your wishlist.';
      if (modalActions) {
        modalActions.innerHTML = 
          '<button type="button" class="cart-drawer__modal-btn cart-drawer__modal-btn--secondary" id="confirm-yes-btn">Yes</button>' +
          '<button type="button" class="cart-drawer__modal-btn cart-drawer__modal-btn--primary" id="confirm-no-btn">No</button>';
      }
    } else {
      // Move to wishlist option for cart
      if (modalTitle) modalTitle.textContent = 'Removing from cart?';
      if (modalSub) modalSub.innerHTML = 'You can instead track when there are any offers by <strong>moving it to wishlist</strong> instead';
      if (modalActions) {
        modalActions.innerHTML = 
          '<button type="button" class="cart-drawer__modal-btn cart-drawer__modal-btn--secondary" id="confirm-remove-btn">Remove</button>' +
          '<button type="button" class="cart-drawer__modal-btn cart-drawer__modal-btn--primary" id="confirm-move-btn">Move instead</button>';
      }
    }
    
    // Re-attach event listeners after innerHTML change
    attachModalButtonListeners();
    
    if (drawerModalOverlay) {
      drawerModalOverlay.classList.add('is-open');
      drawerModalOverlay.setAttribute('aria-hidden', 'false');
    }
  }

  function closeRemoveModal() {
    if (drawerModalOverlay) {
      drawerModalOverlay.classList.remove('is-open');
      drawerModalOverlay.setAttribute('aria-hidden', 'true');
    }
    pendingRemove = null;
  }

  /* ============================================
     MONEY FORMATTER
     ============================================ */

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    amount = amount.replace(/\.00$/, '');
    return customerData.moneyFormat.replace(/\{\{[^}]*\}\}/, amount);
  }

  function cleanVariantTitle(title) {
    if (!title) return '';
    // Remove " / Female" or "/ Female" from the title
    return title.replace(/\s*\/\s*Female/gi, '').trim();
  }

  /* ============================================
     SHOPIFY AJAX CART API
     ============================================ */

  function fetchCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }).then(function (r) { return r.ok ? r.json() : Promise.reject(r); });
  }

  // Section IDs we ask Shopify to render alongside cart mutations so we can
  // refresh the drawer markup + totals in a single round-trip (Section
  // Rendering API). Equivalent to an `INSERT ... RETURNING` on the cart.
  var CART_RESPONSE_SECTIONS = ['ajax-cart-items', 'ajax-cart-totals'];

  function addToCart(variantId, quantity, properties, opts) {
    var item = { id: variantId, quantity: quantity || 1 };
    if (properties && Object.keys(properties).length) item.properties = properties;
    var body = { items: [item] };
    if (opts && opts.sections) body.sections = opts.sections.join(',');
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.ok ? r.json() : Promise.reject(r); });
  }

  function changeCartItem(lineKey, quantity, opts) {
    var body = { id: lineKey, quantity: quantity };
    if (opts && opts.sections) body.sections = opts.sections.join(',');
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.ok ? r.json() : Promise.reject(r); });
  }

  function changeCartItemVariant(lineKey, quantity, variantId) {
    // Shopify doesn't support changing variants directly via the API
    // We need to remove the old item and add the new variant. Pipe sections
    // through the final add so the drawer markup + totals refresh in the
    // same round-trip.
    return changeCartItem(lineKey, 0).then(function() {
      return addToCart(variantId, quantity, null, { sections: CART_RESPONSE_SECTIONS });
    });
  }

  /* ============================================
     CART RESPONSE / SECTIONS HELPERS
     ============================================
     The Section Rendering API on /cart/add.js and /cart/change.js returns
     pre-rendered HTML for any sections we ask for. We use that to update the
     cart-items list and totals in one network call instead of a follow-up
     /cart.js GET. lastKnownCart is the source of truth for checkout gating
     so it doesn't depend on which drawer state is currently rendered. */

  var lastKnownCart = null;          // last full /cart.js JSON we saw
  var lastKnownTotals = null;        // { total_price, item_count } from sections= response
  var isAdding = false;              // gate: an Add-to-Cart POST is in flight
  var pendingMutation = null;        // gate: a /cart/change.js POST is in flight

  function parseSectionsTotals(sections) {
    if (!sections || !sections['ajax-cart-totals']) return null;
    try {
      var tmp = document.createElement('div');
      tmp.innerHTML = sections['ajax-cart-totals'];
      var el = tmp.querySelector('#ajax-cart-totals');
      if (!el) return null;
      return {
        total_price: parseInt(el.getAttribute('data-cart-total'), 10) || 0,
        item_count: parseInt(el.getAttribute('data-cart-count'), 10) || 0
      };
    } catch (e) { return null; }
  }

  // Shake a single size component to draw attention to a "select a size"
  // error. Cancels any previous shake first so only one element shakes at a
  // time. `el` should be the visible size component (the radio bar, the
  // dropdown, or the size-selector wrapper). Exposed on window so other
  // scripts (product-info.liquid, lookbook-scrapbook.liquid) can call it.
  var _shakingEl = null;
  var _shakeTimer = null;
  function shakeSizeComponent(el) {
    if (!el) return;
    if (_shakingEl && _shakingEl !== el) {
      _shakingEl.classList.remove('is-shaking');
    }
    clearTimeout(_shakeTimer);
    // Reflow so re-triggering on the same element restarts the animation.
    el.classList.remove('is-shaking');
    // eslint-disable-next-line no-unused-expressions
    void el.offsetWidth;
    el.classList.add('is-shaking');
    _shakingEl = el;
    _shakeTimer = setTimeout(function () {
      if (_shakingEl) _shakingEl.classList.remove('is-shaking');
      _shakingEl = null;
    }, 500);
  }
  window.shakeSizeComponent = shakeSizeComponent;

  // Inject (or update) a short notice inside the cart-item for `lineKey`.
  // Used to surface "this is all we have currently" when the line's qty was
  // clamped to the available-variants count after a pending-size pick.
  // Idempotent: re-runs replace the existing notice rather than stacking.
  function showCartItemNotice(lineKey, message) {
    if (!lineKey) return;
    var items = drawer.querySelectorAll('[data-cart-item][data-line-key="' + lineKey + '"]');
    items.forEach(function (item) {
      var details = item.querySelector('.cart-item__details') || item;
      var existing = details.querySelector('.cart-item__notice');
      if (existing) { existing.textContent = message; return; }
      var p = document.createElement('p');
      p.className = 'cart-item__notice';
      p.setAttribute('role', 'status');
      p.textContent = message;
      var actions = details.querySelector('.cart-item__actions');
      if (actions) details.insertBefore(p, actions);
      else details.appendChild(p);
    });
  }

  function applyCartSections(sections) {
    if (!sections) return;

    if (sections['ajax-cart-items'] && cartItemsList) {
      var tmp = document.createElement('div');
      tmp.innerHTML = sections['ajax-cart-items'];
      var inner = tmp.querySelector('#ajax-cart-inner');
      cartItemsList.innerHTML = inner ? inner.innerHTML : tmp.innerHTML;
    }

    var totals = parseSectionsTotals(sections);
    if (totals) {
      lastKnownTotals = totals;
      if (quickCheckoutTotal) quickCheckoutTotal.textContent = formatMoney(totals.total_price);
      if (cartCheckoutTotal) cartCheckoutTotal.textContent = formatMoney(totals.total_price);
      updateHeaderCartCount(totals.item_count);

      // Empty-state toggling normally handled by renderCartItems; mirror it
      // here since we're bypassing that path.
      if (cartEmpty && cartFooter) {
        if (totals.item_count === 0) {
          cartItemsList.innerHTML = '';
          cartEmpty.style.display = '';
          cartFooter.style.display = 'none';
          if (cartRecs) cartRecs.style.display = 'none';
        } else {
          cartEmpty.style.display = 'none';
          cartFooter.style.display = '';
        }
      }
    }
  }

  /* ============================================
     BUY-IT-NOW CART SNAPSHOT / RESTORE
     ============================================
     Razorpay Quick Checkout (and Shopify dynamic checkout buttons in
     general) clear the cart and add the buy-now item before opening
     their flow. We snapshot the cart on click and restore it after,
     regardless of whether the checkout was completed or abandoned. */

  var BUYNOW_SNAPSHOT_KEY = 'rachawk_buynow_cart_snapshot';

  function snapshotCartForBuyNow() {
    return fetchCart().then(function (cart) {
      var items = (cart.items || []).map(function (i) {
        var entry = { id: i.id, quantity: i.quantity };
        if (i.properties && Object.keys(i.properties).length) entry.properties = i.properties;
        if (i.selling_plan_allocation && i.selling_plan_allocation.selling_plan) {
          entry.selling_plan = i.selling_plan_allocation.selling_plan.id;
        }
        return entry;
      });
      try {
        localStorage.setItem(BUYNOW_SNAPSHOT_KEY, JSON.stringify({ items: items, ts: Date.now() }));
      } catch (e) { /* storage full / disabled */ }
    }).catch(function () { /* ignore */ });
  }

  function discardBuyNowSnapshot() {
    try { localStorage.removeItem(BUYNOW_SNAPSHOT_KEY); } catch (e) {}
  }

  function restoreCartFromBuyNowSnapshot() {
    var raw;
    try { raw = localStorage.getItem(BUYNOW_SNAPSHOT_KEY); } catch (e) { return Promise.resolve(false); }
    if (!raw) return Promise.resolve(false);

    var snap;
    try { snap = JSON.parse(raw); } catch (e) {
      discardBuyNowSnapshot();
      return Promise.resolve(false);
    }

    // Empty snapshot → nothing to restore; just drop it.
    if (!snap.items || snap.items.length === 0) {
      discardBuyNowSnapshot();
      return Promise.resolve(false);
    }

    return fetch('/cart/clear.js', { method: 'POST' })
      .then(function (r) { if (!r.ok) throw r; })
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: snap.items })
        });
      })
      .then(function (r) {
        if (!r || !r.ok) throw r;
        // Only drop the snapshot once the restore actually succeeded; if any
        // step 4xx'd we keep it around for the next attempt.
        discardBuyNowSnapshot();
        return true;
      })
      .catch(function () { return false; });
  }

  function isRazorpayModalPresent() {
    // NOTE: #magic-checkout-data-container is permanent on the page (it's a
    // data bridge injected by the Magic Checkout script at load time, not
    // when the flow opens), so we can't use it as a signal.
    //
    // Magic Checkout itself doesn't embed an in-page iframe — it redirects
    // the user to api.razorpay.com/v1/standard_checkout/... — so on cancel
    // the user comes back via a fresh navigation, not bfcache, and the
    // watcher below is only relevant for the classic Razorpay iframe flow.

    var elements = document.querySelectorAll(
      '.razorpay-container, ' +
      '.razorpay-checkout-frame, ' +
      'iframe[src*="razorpay"], ' +
      'iframe[name*="razorpay"], ' +
      'iframe[src*="magic.razorpay"]'
    );

    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      var style = window.getComputedStyle(el);
      if (
        el.offsetWidth > 0 &&
        el.offsetHeight > 0 &&
        style.display !== 'none' &&
        style.visibility !== 'hidden'
      ) {
        return true;
      }
    }

    // Body-class scroll lock as a soft signal.
    var bodyLocked =
      document.body &&
      (document.body.classList.contains('magic-checkout-open') ||
        document.body.classList.contains('rzp-checkout-open') ||
        document.body.classList.contains('razorpay-checkout-open'));
    if (bodyLocked) return true;

    return false;
  }

  var buynowWatchTimer = null;
  var buynowModalSeen = false;
  // Set true the moment a Buy Now click is captured; cleared when the watcher
  // resolves (modal opened-then-closed, or timeout). While true, no other
  // codepath is allowed to touch the cart — Razorpay/Shopify owns it.
  var buynowClickInFlight = false;

  function triggerRestoreAndRefresh() {
    return restoreCartFromBuyNowSnapshot().then(function (restored) {
      if (restored && typeof refreshCart === 'function') refreshCart();
      return restored;
    });
  }

  function startBuyNowWatcher() {
    if (buynowWatchTimer) return;
    buynowModalSeen = false;
    var startedAt = Date.now();
    var lastPresentAt = 0;

    // Hard upper bound — Magic Checkout users can sit on the address/OTP
    // step for several minutes. Don't pre-empt them.
    var MAX_WATCH_MS = 15 * 60 * 1000;          // 15 min
    // If the modal hasn't been *detected* once in this window, treat as a
    // failed open (Razorpay errored before rendering anything).
    var NO_MODAL_FAIL_MS = 30 * 1000;           // 30 s
    // After we last saw the modal, how long without seeing it counts as
    // "definitely closed" (guards against brief reflows / iframe swaps
    // during the OTP step).
    var CLOSE_CONFIRM_MS = 1500;                // 1.5 s

    buynowWatchTimer = setInterval(function () {
      var hasSnapshot;
      try { hasSnapshot = !!localStorage.getItem(BUYNOW_SNAPSHOT_KEY); } catch (e) { hasSnapshot = false; }
      if (!hasSnapshot) {
        clearInterval(buynowWatchTimer);
        buynowWatchTimer = null;
        buynowClickInFlight = false;
        return;
      }

      var now = Date.now();
      var present = isRazorpayModalPresent();

      if (present) {
        buynowModalSeen = true;
        lastPresentAt = now;
        return;
      }

      if (buynowModalSeen) {
        // Modal was visible and is no longer visible. Wait a beat to make
        // sure it's not a transient re-mount, then restore.
        if (now - lastPresentAt < CLOSE_CONFIRM_MS) return;

        clearInterval(buynowWatchTimer);
        buynowWatchTimer = null;
        buynowClickInFlight = false;
        triggerRestoreAndRefresh();
        return;
      }

      // Modal has never been detected.
      if (now - startedAt > NO_MODAL_FAIL_MS) {
        // Either the click did nothing (no purchase fired, cart untouched →
        // restore is a no-op), or Razorpay errored before rendering (its own
        // cart/clear may have run, leaving the cart empty → restore needed).
        // In either case, restoring from snapshot puts the user back in a
        // correct state.
        clearInterval(buynowWatchTimer);
        buynowWatchTimer = null;
        buynowClickInFlight = false;
        triggerRestoreAndRefresh();
        return;
      }

      if (now - startedAt > MAX_WATCH_MS) {
        // Give up entirely — preserve the snapshot for a future pageshow
        // restore rather than racing whatever's left of the Razorpay flow.
        clearInterval(buynowWatchTimer);
        buynowWatchTimer = null;
        buynowClickInFlight = false;
      }
    }, 400);
  }

  document.addEventListener('click', function (e) {
    // Only match the real dynamic-checkout button — never the locked overlay
    // (no purchase fires, so we'd snapshot for nothing and leave it stale).
    var btn = e.target.closest(
      '.shopify-payment-button__button, ' +
      '.shopify-payment-button button, ' +
      '.lookbook-dynamic-btn-wrapper button, ' +
      '.rzp-custom-buy-now'
    );
    if (!btn) return;
    if (btn.closest('.product-info__buy-now--locked')) return;
    buynowClickInFlight = true;
    snapshotCartForBuyNow().then(startBuyNowWatcher);
  }, true);

  // Restore-on-return handler.
  //
  // Magic Checkout redirects to api.razorpay.com instead of embedding an
  // in-page modal, so on cancel/back the user lands here via a fresh
  // navigation (pageshow.persisted === false). We therefore restore on
  // every page load when a recent snapshot exists, gated by:
  //
  //  - TTL (snapshot older than BUYNOW_SNAPSHOT_TTL_MS is treated as
  //    abandoned/stale and discarded without writing the cart — protects
  //    against zombie snapshots from days-old failed attempts).
  //
  //  - "Are we on a post-success page?" check — if the current URL looks
  //    like a Shopify order/thank-you page, the user just paid and we must
  //    NOT undo that by restoring the pre-buy-now cart. The redirect-back
  //    from a successful Magic Checkout lands on /thank_you or /orders/...
  //    so this skip is critical.
  //
  //  - buynowClickInFlight guard for the in-page iframe flow (where the
  //    watcher owns the restore).
  var BUYNOW_SNAPSHOT_TTL_MS = 30 * 60 * 1000; // 30 min

  function snapshotTimestamp() {
    try {
      var raw = localStorage.getItem(BUYNOW_SNAPSHOT_KEY);
      if (!raw) return 0;
      var snap = JSON.parse(raw);
      return (snap && snap.ts) || 0;
    } catch (e) { return 0; }
  }

  function isPostCheckoutPage() {
    var p = (location.pathname || '').toLowerCase();
    return (
      p.indexOf('/thank_you') !== -1 ||
      p.indexOf('/thank-you') !== -1 ||
      p.indexOf('/orders/') !== -1 ||
      p.indexOf('/checkouts/') !== -1 ||
      p.indexOf('/checkout/') !== -1
    );
  }

  function maybeRestoreOnLoad() {
    if (buynowClickInFlight) return;
    var ts = snapshotTimestamp();
    if (!ts) return;
    if (Date.now() - ts > BUYNOW_SNAPSHOT_TTL_MS) {
      discardBuyNowSnapshot();
      return;
    }
    if (isPostCheckoutPage()) {
      // User completed the purchase — drop the snapshot, do NOT restore.
      discardBuyNowSnapshot();
      return;
    }
    triggerRestoreAndRefresh();
  }

  // Fires on every navigation (incl. fresh load AND bfcache restore).
  window.addEventListener('pageshow', maybeRestoreOnLoad);

  /* ============================================
     PRODUCT API (for variants)
     ============================================ */

  // Pre-seed the variants cache from the inlined #cart-drawer-product-data
  // JSON so the PDP/lookbook Add-to-Cart paths never need the
  // /products/<handle>.js pre-flight.
  try {
    var _seedEl = document.getElementById('cart-drawer-product-data');
    if (_seedEl) {
      var _seed = JSON.parse(_seedEl.textContent);
      if (_seed && _seed.handle && _seed.variants) {
        productVariantsCache[_seed.handle] = {
          variants: _seed.variants,
          options: _seed.options || []
        };
      }
    }
  } catch (e) { /* noop */ }

  function fetchProductVariants(handle) {
    if (productVariantsCache[handle]) {
      return Promise.resolve(productVariantsCache[handle]);
    }

    // Clean the handle - remove any query parameters or fragments
    var cleanHandle = handle.split('?')[0].split('#')[0];
    if (productVariantsCache[cleanHandle]) {
      return Promise.resolve(productVariantsCache[cleanHandle]);
    }

    return fetch('/products/' + cleanHandle + '.js')
      .then(function (r) {
        if (!r.ok) {
          throw new Error('Failed to fetch product: ' + r.status);
        }
        return r.json();
      })
      .then(function (product) {
        // Cache the full product data including options
        productVariantsCache[handle] = {
          variants: product.variants || [],
          options: product.options || []
        };
        return productVariantsCache[handle];
      })
      .catch(function (err) {
        console.error('Error fetching variants for ' + handle + ':', err);
        return { variants: [], options: [] };
      });
  }

  /* ============================================
     WISHLIST (LocalStorage)
     ============================================ */

  const WISHLIST_KEY = 'rachawk_wishlist';

  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(items) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    updateWishlistCount();
  }

  function addToWishlist(product) {
    var items = getWishlist();
    if (!items.find(function (i) { return i.handle === product.handle; })) {
      items.push({
        handle: product.handle,
        title: product.title,
        description: product.description || '',
        price: product.price || 0,
        priceFormatted: product.priceFormatted || formatMoney(product.price || 0),
        image: product.image || '',
        url: product.url || '/products/' + product.handle,
        label: product.label || '',
        selectedVariantId: product.selectedVariantId || null
      });
      saveWishlist(items);
    }
  }

  function removeFromWishlist(handle) {
    var items = getWishlist();
    items = items.filter(function (i) { return i.handle !== handle; });
    saveWishlist(items);
  }

  function isInWishlist(handle) {
    return getWishlist().some(function (i) { return i.handle === handle; });
  }

  function updateWishlistVariant(handle, variantId) {
    var items = getWishlist();
    var item = items.find(function (i) { return i.handle === handle; });
    if (item) {
      item.selectedVariantId = variantId;
      saveWishlist(items);
    }
  }

  function updateWishlistCount() {
    var count = getWishlist().length;
    var badge = document.querySelector('.header__wishlist-count');
    if (badge) {
      badge.textContent = count;
      badge.style.display = count > 0 ? '' : 'none';
    }
  }

  /* ============================================
     RENDER: STATE 1 (Added to Cart item)
     ============================================ */

  function renderAddedItem(itemData) {
    var tagHtml = '';
    if (itemData.label) {
      tagHtml = '<div class="cart-item__craft-label"><span class="cart-item__craft-label-text">' + escapeHtml(itemData.label) + '</span></div>';
    }

    var variantHtml = '';
    if (itemData.variantTitle && itemData.variantTitle !== 'Default Title') {
      variantHtml = '<p class="cart-item__variant">' + escapeHtml(cleanVariantTitle(itemData.variantTitle)) + '</p>';
    }

    // NEW: Ensure we have a valid URL
    var productUrl = itemData.url || (itemData.handle ? '/products/' + itemData.handle : '#');

    addedItemContainer.innerHTML =
      '<div class="cart-item" data-cart-item data-line-key="' + (itemData.key || '') + '">' +
        tagHtml +
        '<div class="cart-item__image-wrap">' +
          '<a href="' + productUrl + '">' +
            '<img src="' + escapeHtml(itemData.image) + '" alt="' + escapeHtml(itemData.title) + '" width="240" height="312" class="cart-item__image">' +
          '</a>' +
        '</div>' +
        '<div class="cart-item__details">' +
          '<h4 class="cart-item__name"><a href="' + productUrl + '">' + escapeHtml(itemData.title) + '</a></h4>' +
          (itemData.description ? '<p class="cart-item__desc">' + escapeHtml(itemData.description) + '</p>' : '') +
          '<p class="cart-item__price">' + (itemData.priceFormatted || formatMoney(itemData.price)) + '</p>' +
          variantHtml +
          '<div class="cart-item__actions">' +
            '<div class="cart-item__qty" data-qty-wrap>' +
              '<button type="button" class="cart-item__qty-btn" data-qty-change="-1" data-line-key="' + (itemData.key || '') + '" aria-label="Decrease quantity">−</button>' +
              '<input type="number" class="cart-item__qty-val" value="' + (itemData.quantity || 1) + '" min="1" data-line-key="' + (itemData.key || '') + '" aria-label="Quantity" readonly>' +
              '<button type="button" class="cart-item__qty-btn" data-qty-change="1" data-line-key="' + (itemData.key || '') + '" aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button type="button" class="cart-item__remove" data-remove-item data-line-key="' + (itemData.key || '') + '" data-product-title="' + escapeHtml(itemData.title) + '" data-product-handle="' + escapeHtml(itemData.handle || '') + '" aria-label="Remove">' +
              '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
            '</button>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  /* ============================================
     RENDER: STATE 2 (Full Cart)
     ============================================ */

  function renderCartItems(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      cartItemsList.innerHTML = '';
      cartEmpty.style.display = '';
      cartFooter.style.display = 'none';
      cartRecs.style.display = 'none';
      return;
    }

    cartEmpty.style.display = 'none';
    cartFooter.style.display = '';

    var html = '';
    var pendingSizeItems = [];
    cart.items.forEach(function (item) {
      var tagHtml = '';
      if (item.properties && item.properties._label) {
        tagHtml = '<div class="cart-item__craft-label"><span class="cart-item__craft-label-text">' + escapeHtml(item.properties._label) + '</span></div>';
      }

      var isPendingSize = item.properties && item.properties._is_unselected === 'true';

      var variantHtml = '';
      if (isPendingSize) {
        variantHtml = '<div class="cart-item__size-selector" data-pending-size data-line-key="' + item.key + '" data-handle="' + escapeHtml(item.handle) + '">' +
          '<select class="cart-item__size-select" data-line-key="' + item.key + '" data-current-qty="' + item.quantity + '" aria-label="Select size"><option value="" disabled selected>Select size</option></select>' +
          '<p class="cart-item__size-error" style="display: none;">⚠ Please select a size</p>' +
        '</div>';
        pendingSizeItems.push({ key: item.key, handle: item.handle });
      } else if (item.variant_title && item.variant_title !== 'Default Title') {
        variantHtml = '<p class="cart-item__variant">' + escapeHtml(item.variant_title) + '</p>';
      }

      var descHtml = '';
      if (item.product_description) {
        descHtml = '<p class="cart-item__desc">' + escapeHtml(truncateText(stripHtml(item.product_description), 60)) + '</p>';
      }

      html +=
        '<div class="cart-item" data-cart-item data-line-key="' + item.key + '">' +
          tagHtml +
          '<div class="cart-item__image-wrap">' +
            '<a href="' + escapeHtml(item.url) + '">' +
              '<img src="' + getSizedImageUrl(item.image, '240x') + '" alt="' + escapeHtml(item.title) + '" width="240" height="312" loading="lazy" class="cart-item__image">' +
            '</a>' +
          '</div>' +
          '<div class="cart-item__details">' +
            '<h4 class="cart-item__name"><a href="' + escapeHtml(item.url) + '">' + escapeHtml(item.product_title) + '</a></h4>' +
            descHtml +
            '<p class="cart-item__price">' + formatMoney(item.final_line_price) + '</p>' +
            variantHtml +
            '<div class="cart-item__actions">' +
              '<div class="cart-item__qty" data-qty-wrap>' +
                '<button type="button" class="cart-item__qty-btn" data-qty-change="-1" data-line-key="' + item.key + '" aria-label="Decrease quantity">−</button>' +
                '<input type="number" class="cart-item__qty-val" value="' + item.quantity + '" min="1" data-line-key="' + item.key + '" aria-label="Quantity" readonly>' +
                '<button type="button" class="cart-item__qty-btn" data-qty-change="1" data-line-key="' + item.key + '" aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<button type="button" class="cart-item__remove" data-remove-item data-line-key="' + item.key + '" data-product-title="' + escapeHtml(item.product_title) + '" data-product-handle="' + escapeHtml(item.handle) + '" aria-label="Remove ' + escapeHtml(item.product_title) + '">' +
                '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
              '</button>' +
            '</div>' +
          '</div>' +
        '</div>';
    });

    cartItemsList.innerHTML = html;
    cartCheckoutTotal.textContent = formatMoney(cart.total_price);

    // Hydrate pending-size dropdowns: fetch variants per product, populate options, hook change.
    pendingSizeItems.forEach(function (entry) {
      var wrap = cartItemsList.querySelector('[data-pending-size][data-line-key="' + entry.key + '"]');
      if (!wrap) return;
      var select = wrap.querySelector('select');
      if (!select) return;

      fetchProductVariants(entry.handle).then(function (data) {
        var variants = data.variants || [];
        var options = data.options || [];
        var hasSizeOpt = options.some(function (opt) {
          return opt.name && opt.name.toLowerCase() === 'size';
        });
        if (!hasSizeOpt) return;
        var availableVariants = variants.filter(function (v) { return v.available; });
        availableVariants.forEach(function (v) {
          var opt = document.createElement('option');
          opt.value = v.id;
          opt.textContent = cleanVariantTitle(v.title);
          select.appendChild(opt);
        });
      });
    });
  }

  /* ============================================
     RENDER: RECOMMENDATIONS
     ============================================ */

  function renderRecommendations(recs) {
    if (!recs || recs.length === 0) {
      cartRecs.style.display = 'none';
      return;
    }

    cartRecs.style.display = '';
    var html = '';
    recs.forEach(function (rec) {
      var wished = isInWishlist(rec.handle);
      var tagHtml = rec.label
        ? '<div class="rec-card__craft-label"><span class="rec-card__craft-label-text">' + escapeHtml(rec.label) + '</span></div>'
        : '';

      html +=
        '<div class="rec-card" data-rec-card data-product-handle="' + escapeHtml(rec.handle) + '">' +
          tagHtml +
          '<div class="rec-card__image-wrap">' +
            '<button type="button" class="rec-card__wishlist' + (wished ? ' is-wished' : '') + '" data-wishlist-toggle="' + escapeHtml(rec.handle) + '" aria-label="Add ' + escapeHtml(rec.title) + ' to wishlist">' +
              '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
            '</button>' +
            '<a href="' + (rec.url || '/products/' + rec.handle) + '">' +
              '<img src="' + escapeHtml(rec.image) + '" alt="' + escapeHtml(rec.title) + '" width="400" height="533" loading="lazy" class="rec-card__image">' +
            '</a>' +
          '</div>' +
          '<div class="rec-card__body">' +
            '<h4 class="rec-card__name">' + escapeHtml(rec.title) + '</h4>' +
            (rec.description ? '<p class="rec-card__desc">' + escapeHtml(rec.description) + '</p>' : '') +
            '<p class="rec-card__price">' + (rec.priceFormatted || formatMoney(rec.price)) + '</p>' +
          '</div>' +
        '</div>';
    });

    cartRecsGrid.innerHTML = html;
  }

  function showCartRecommendations() {
    var recs = null;
    
    // 1. Try to use recommendations from the most recently added product
    if (lastAddedProductData && lastAddedProductData.recommendations) {
      recs = lastAddedProductData.recommendations;
    } 
    // 2. Fallback to the current page's product data if available in the DOM
    else {
      try {
        var el = document.getElementById('cart-drawer-product-data');
        if (el) {
          var pageProductData = JSON.parse(el.textContent);
          if (pageProductData && pageProductData.recommendations) {
            recs = pageProductData.recommendations;
          }
        }
      } catch (err) {
        console.warn('Could not parse default recommendations:', err);
      }
    }

    // Render if we found recommendations
    if (recs) {
      renderRecommendations(recs);
    }
  }

  /* ============================================
     RENDER: WISHLIST ITEMS
     ============================================ */

  function renderWishlistItems() {
    var items = getWishlist();

    if (items.length === 0) {
      wishlistItemsList.innerHTML = '';
      if (wishlistEmpty) wishlistEmpty.style.display = '';
      return;
    }

    if (wishlistEmpty) wishlistEmpty.style.display = 'none';

    var html = '';
    items.forEach(function (item) {
      var tagHtml = item.label
        ? '<div class="wishlist-item__craft-label"><span class="wishlist-item__craft-label-text">' + escapeHtml(item.label) + '</span></div>'
        : '';

      var descHtml = item.description
        ? '<p class="wishlist-item__desc">' + escapeHtml(truncateText(item.description, 50)) + '</p>'
        : '';

      html +=
        '<div class="wishlist-item" data-wishlist-item data-product-handle="' + escapeHtml(item.handle) + '">' +
          tagHtml +
          '<div class="wishlist-item__image-wrap">' +
            '<a href="' + escapeHtml(item.url) + '">' +
              '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '" width="180" height="231" loading="lazy" class="wishlist-item__image">' +
            '</a>' +
          '</div>' +
          '<div class="wishlist-item__details">' +
            '<h4 class="wishlist-item__name"><a href="' + escapeHtml(item.url) + '">' + escapeHtml(item.title) + '</a></h4>' +
            descHtml +
            '<p class="wishlist-item__price">' + (item.priceFormatted || formatMoney(item.price)) + '</p>' +
            '<div class="wishlist-item__size-selector">' +
              '<select class="wishlist-item__size-select" data-wishlist-size-select data-product-handle="' + escapeHtml(item.handle) + '" aria-label="Select size">' +
                '<option value="">Select size</option>' +
              '</select>' +
              '<span class="wishlist-item__size-error" style="display:none;">Please select a size</span>' +
            '</div>' +
            '<div class="wishlist-item__actions">' +
              '<button type="button" class="wishlist-item__move-to-cart" data-wishlist-move-to-cart data-product-handle="' + escapeHtml(item.handle) + '" aria-label="Move ' + escapeHtml(item.title) + ' to cart">Move to Cart</button>' +
              '<button type="button" class="wishlist-item__remove" data-wishlist-remove="' + escapeHtml(item.handle) + '" aria-label="Remove ' + escapeHtml(item.title) + ' from wishlist">' +
                '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
              '</button>' +
            '</div>' +
          '</div>' +
        '</div>';
    });

    wishlistItemsList.innerHTML = html;

    // Load variants for each product
    items.forEach(function (item) {
      loadWishlistItemVariants(item.handle, item.selectedVariantId);
    });
  }

  function loadWishlistItemVariants(handle, selectedVariantId) {
    var wishlistItem = wishlistItemsList.querySelector('[data-wishlist-item][data-product-handle="' + handle + '"]');
    if (!wishlistItem) {
      console.warn('Wishlist item not found for handle:', handle);
      return;
    }

    var sizeSelector = wishlistItem.querySelector('.wishlist-item__size-selector');
    var select = wishlistItem.querySelector('[data-wishlist-size-select]');
    var moveToCartBtn = wishlistItem.querySelector('[data-wishlist-move-to-cart]');
    
    if (!select || !sizeSelector) {
      console.warn('Size selector elements not found for handle:', handle);
      return;
    }

    console.log('Loading variants for:', handle);

    fetchProductVariants(handle).then(function (productData) {
      var variants = productData.variants || [];
      var options = productData.options || [];
      
      console.log('Fetched product data for ' + handle + ':', { variants: variants, options: options });
      
      if (!variants || variants.length === 0) {
        console.warn('No variants found for:', handle);
        // Hide size selector if no variants
        sizeSelector.style.display = 'none';
        return;
      }

      // Check if product has a "Size" option
      var hasSizeOption = options.some(function(opt) {
        return opt.name && opt.name.toLowerCase() === 'size';
      });

      console.log('Has Size option:', hasSizeOption);

      // If no Size option exists, hide the selector and auto-select the variant
      if (!hasSizeOption) {
        console.log('No Size option found, hiding size selector');
        sizeSelector.style.display = 'none';
        
        // Auto-select the first available variant
        var firstAvailable = variants.find(function(v) { return v.available; });
        if (firstAvailable) {
          updateWishlistVariant(handle, firstAvailable.id);
        } else {
          // All variants out of stock
          if (moveToCartBtn) {
            moveToCartBtn.disabled = true;
            moveToCartBtn.textContent = 'Out of Stock';
          }
        }
        return;
      }

      // Product has Size option - proceed with size selector logic
      // Filter to only available variants
      var availableVariants = variants.filter(function(v) { return v.available; });
      
      // If no available variants, hide selector and show out of stock
      if (availableVariants.length === 0) {
        console.log('No available variants, hiding size selector');
        sizeSelector.style.display = 'none';
        if (moveToCartBtn) {
          moveToCartBtn.disabled = true;
          moveToCartBtn.textContent = 'Out of Stock';
        }
        return;
      }

      // If only one available variant, hide selector and auto-select
      if (availableVariants.length === 1) {
        console.log('Single available variant, hiding size selector and auto-selecting');
        sizeSelector.style.display = 'none';
        updateWishlistVariant(handle, availableVariants[0].id);
        return;
      }

      // Multiple available variants with Size option - show size selector
      console.log('Multiple variants available with Size option, showing size selector');
      sizeSelector.style.display = '';

      // Clear existing options except placeholder
      select.innerHTML = '<option value="">Select size</option>';

      // Add available variants
      availableVariants.forEach(function (v) {
        var opt = document.createElement('option');
        opt.value = v.id;
        opt.textContent = cleanVariantTitle(v.title);
        if (selectedVariantId && v.id === selectedVariantId) {
          opt.selected = true;
        }
        select.appendChild(opt);
      });

      console.log('Added ' + availableVariants.length + ' variant options');

      // If we had a selected variant, make sure it's still selected
      if (selectedVariantId) {
        select.value = selectedVariantId;
      }
    }).catch(function(err) {
      console.error('Error in loadWishlistItemVariants:', err);
      // Hide size selector on error
      sizeSelector.style.display = 'none';
    });
  }

  /* ============================================
     REFRESH FULL CART (after any change)
     ============================================ */

  function refreshCart() {
    cartLoading.classList.add('is-visible');
    
    var cartJsonPromise = fetchCart();
    var cartHtmlPromise = fetch('/?section_id=ajax-cart-items').then(function(r) { return r.ok ? r.text() : Promise.reject(r); });

    return Promise.all([cartJsonPromise, cartHtmlPromise]).then(function (results) {
      var cart = results[0];
      var htmlText = results[1];
      
      cartLoading.classList.remove('is-visible');

      if (!cart || !cart.items || cart.items.length === 0) {
        cartItemsList.innerHTML = '';
        cartEmpty.style.display = '';
        cartFooter.style.display = 'none';
        cartRecs.style.display = 'none';
      } else {
        cartEmpty.style.display = 'none';
        cartFooter.style.display = '';
        
        var tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlText;
        var inner = tempDiv.querySelector('#ajax-cart-inner');
        
        if (inner) {
          cartItemsList.innerHTML = inner.innerHTML;
        } else {
          renderCartItems(cart);
        }
      }

      quickCheckoutTotal.textContent = formatMoney(cart.total_price);
      cartCheckoutTotal.textContent = formatMoney(cart.total_price);
      updateHeaderCartCount(cart.item_count);
      lastKnownCart = cart;
      return cart;
    });
  }

  function updateHeaderCartCount(count) {
    var badge = document.querySelector('.header__cart-count');
    if (badge) {
      badge.textContent = count;
      badge.style.display = count > 0 ? '' : 'none';
    }
  }

  /* ============================================
     EVENT: ADD TO CART (intercept form submit)
     ============================================ */

  function handleAddToCart(e) {
    e.preventDefault();

    // Double-submit guard. Until the in-flight POST /cart/add.js settles, any
    // further clicks no-op. Without this a laggy network turns two clicks into
    // two distinct line additions on the Shopify cart.
    if (isAdding) return;

    var form = e.target.closest('form[data-product-form]') || e.target;
    if (!form || form.tagName !== 'FORM') return;

    var variantInput = form.querySelector('[data-variant-id]') || form.querySelector('input[name="id"]');
    if (!variantInput) return;

    var variantId = parseInt(variantInput.value, 10);
    var qtyInput = form.querySelector('[data-qty-input]') || form.querySelector('input[name="quantity"]');
    var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

    var productData = null;
    try {
      var el = document.getElementById('cart-drawer-product-data');
      if (el) productData = JSON.parse(el.textContent);
    } catch (err) { /* noop */ }

    var itemData = {
      title: productData ? productData.title : 'Product',
      description: productData ? productData.description : '',
      price: productData ? productData.price : 0,
      priceFormatted: productData ? productData.priceFormatted : '',
      image: productData ? productData.image : '',
      variantTitle: '',
      label: productData ? productData.label : '',
      handle: productData ? productData.handle : '',
      quantity: quantity,
      key: ''
    };

    var selectedOptions = [];
    form.querySelectorAll('.product-info__size-btn input:checked').forEach(function (r) {
      selectedOptions.push(r.value);
    });
    if (selectedOptions.length > 0) {
      itemData.variantTitle = selectedOptions.join(' / ');
    }

    // Detect missing size selection across ONLY the visible (non-hidden) option groups.
    var visibleOptionGroups = form.querySelectorAll('.product-info__option:not(.product-info__option--hidden)');
    var visibleMissingSize = false;
    for (var vi = 0; vi < visibleOptionGroups.length; vi++) {
      if (!visibleOptionGroups[vi].querySelector('input[type="radio"]:checked')) {
        visibleMissingSize = true;
        break;
      }
    }

    var isLookbook = form.hasAttribute('data-lookbook-form');
    var pdpHandle = productData ? productData.handle : (form.getAttribute('data-product-handle') || '');

    // Decide which variant gets sent to the server and whether the line should
    // be flagged as pending-size (renders as "Select size" in the cart).
    // Variants come from inlined #cart-drawer-product-data (sync, no network).
    var inlinedVariants = (productData && productData.variants) ? productData.variants : null;
    var inlinedOptions  = (productData && productData.options)  ? productData.options  : null;
    var hasSizeOption   = inlinedOptions
      ? inlinedOptions.some(function (o) { return o.name && o.name.toLowerCase() === 'size'; })
      : false;
    var availableVariants = inlinedVariants
      ? inlinedVariants.filter(function (v) { return v.available; })
      : null;

    var addVariantId = variantId;
    var addProperties = null;
    var needsPendingSizeUI = false;

    if (isLookbook && pdpHandle) {
      if (hasSizeOption && inlinedVariants && inlinedVariants.length > 1) {
        // Lookbook tile has no size picker — add a default variant flagged as
        // pending-size, render the in-drawer dropdown.
        var lookbookDefault = (availableVariants && availableVariants[0]) || inlinedVariants[0];
        if (lookbookDefault) addVariantId = lookbookDefault.id;
        addProperties = { _is_unselected: 'true' };
        needsPendingSizeUI = true;
      } else if (inlinedVariants) {
        var firstAvail = inlinedVariants.find(function (v) { return v.available; }) || inlinedVariants[0];
        if (firstAvail) addVariantId = firstAvail.id;
      }
    } else if (visibleMissingSize && pdpHandle && hasSizeOption
               && availableVariants && availableVariants.length > 1) {
      // PDP submitted without picking a size on a multi-size product.
      addProperties = { _is_unselected: 'true' };
      needsPendingSizeUI = true;
    }

    // Visual gate on the CTA itself — moves the perceived latency from
    // "drawer opens stale" to "button is briefly busy". Matches the Buy Now
    // button's "Processing <spinner>" UX so the two CTAs feel consistent.
    //
    // Apply to BOTH the inline Add-to-Cart and the mobile sticky-bar Add-to-Cart
    // (the sticky button just proxies a click to the inline one, so the
    // inline button is what `e.submitter` resolves to — without this the
    // sticky button visually stays idle while the request is in flight).
    var ctaButtons = [];
    var inlineCta = form.querySelector('.product-info__add-to-cart')
      || form.querySelector('button[type="submit"]');
    if (e.submitter) ctaButtons.push(e.submitter);
    if (inlineCta && ctaButtons.indexOf(inlineCta) === -1) ctaButtons.push(inlineCta);
    document.querySelectorAll('[data-sticky-add-to-cart]').forEach(function (btn) {
      if (ctaButtons.indexOf(btn) === -1) ctaButtons.push(btn);
    });

    var ctaOriginals = ctaButtons.map(function (btn) {
      return { disabled: btn.disabled, html: btn.innerHTML };
    });
    ctaButtons.forEach(function (btn) {
      btn.disabled = true;
      btn.classList.add('is-processing');
      btn.innerHTML = 'Processing <span class="btn-spinner" aria-hidden="true"></span>';
      btn.setAttribute('aria-busy', 'true');
    });
    isAdding = true;

    addToCart(addVariantId, quantity, addProperties, { sections: CART_RESPONSE_SECTIONS })
      .then(function (response) {
        if (response.items && response.items.length > 0) {
          itemData.key = response.items[0].key;
          if (needsPendingSizeUI) itemData.pendingSize = true;
        }

        // Update cart list + header count + totals from the same response —
        // no follow-up /cart.js round-trip.
        applyCartSections(response.sections);

        // Build STATE 1 against the now-authoritative server cart.
        if (productData) {
          if (productData.couponTitle) couponTitle.textContent = productData.couponTitle;
          if (productData.couponCode) {
            couponCodeValue.textContent = productData.couponCode;
            couponBox.style.display = '';
          } else {
            couponBox.style.display = 'none';
          }
        }
        lastAddedProductData = productData;

        if (needsPendingSizeUI && availableVariants) {
          renderLookbookSizeSelector(itemData, availableVariants, productData || { handle: pdpHandle });
        } else {
          renderAddedItem(itemData);
        }

        openDrawer('added');

        // Background refresh of lastKnownCart so the next click's pending-size
        // dedupe check (and validateCheckout's source-of-truth check) see the
        // line we just added. Non-blocking; the drawer is already open.
        fetchCart().then(function (cart) { lastKnownCart = cart; }).catch(function () {});
      })
      .catch(function (err) {
        console.error('Add to cart failed:', err);
      })
      .then(function () {
        isAdding = false;
        ctaButtons.forEach(function (btn, i) {
          var orig = ctaOriginals[i];
          btn.disabled = orig.disabled;
          btn.classList.remove('is-processing');
          btn.innerHTML = orig.html;
          btn.removeAttribute('aria-busy');
        });
      });
  }

  function renderLookbookSizeSelector(itemData, availableVariants, productData) {
    var tagHtml = '';
    if (itemData.label) {
      tagHtml = '<div class="cart-item__craft-label"><span class="cart-item__craft-label-text">' + escapeHtml(itemData.label) + '</span></div>';
    }

    var productUrl = itemData.url || (itemData.handle ? '/products/' + itemData.handle : '#');

    var sizeOptionsHtml = '<option value="">Select size</option>';
    availableVariants.forEach(function (v) {
      sizeOptionsHtml += '<option value="' + v.id + '">' + escapeHtml(cleanVariantTitle(v.title)) + '</option>';
    });

    addedItemContainer.innerHTML =
      '<div class="cart-item" data-cart-item data-line-key="">' +
        tagHtml +
        '<div class="cart-item__image-wrap">' +
          '<a href="' + productUrl + '">' +
            '<img src="' + escapeHtml(itemData.image) + '" alt="' + escapeHtml(itemData.title) + '" width="240" height="312" class="cart-item__image">' +
          '</a>' +
        '</div>' +
        '<div class="cart-item__details">' +
          '<h4 class="cart-item__name"><a href="' + productUrl + '">' + escapeHtml(itemData.title) + '</a></h4>' +
          (itemData.description ? '<p class="cart-item__desc">' + escapeHtml(itemData.description) + '</p>' : '') +
          '<p class="cart-item__price">' + (itemData.priceFormatted || formatMoney(itemData.price)) + '</p>' +
          '<div class="cart-item__size-selector" data-lookbook-size>' +
            '<select class="cart-item__size-select" id="lookbook-size-select">' +
              sizeOptionsHtml +
            '</select>' +
          '</div>' +
          '<div class="cart-item__actions">' +
            '<div class="cart-item__qty" data-qty-wrap>' +
              '<button type="button" class="cart-item__qty-btn" data-qty-change="-1" data-line-key="" aria-label="Decrease quantity">−</button>' +
              '<input type="number" class="cart-item__qty-val" value="' + (itemData.quantity || 1) + '" min="1" data-line-key="" aria-label="Quantity" readonly>' +
              '<button type="button" class="cart-item__qty-btn" data-qty-change="1" data-line-key="" aria-label="Increase quantity">+</button>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>';

    var sizeSelect = document.getElementById('lookbook-size-select');
    var currentLineKey = itemData.key || '';
    if (sizeSelect) {
      sizeSelect.addEventListener('change', function () {
        var selectedVariantId = parseInt(sizeSelect.value, 10);
        if (!selectedVariantId) return;

        sizeSelect.disabled = true;

        var selectedVariant = availableVariants.find(function (v) { return v.id === selectedVariantId; });
        itemData.variantTitle = selectedVariant ? cleanVariantTitle(selectedVariant.title) : '';

        var maxQty = availableVariants.length;

        // Authoritative current qty: pull from a fresh /cart.js since the
        // user may have mashed Add-to-Cart and pushed the pending line past
        // the cap. itemData.quantity was set from the PDP form (usually 1)
        // and is stale after repeat adds.
        var qtyPromise;
        if (currentLineKey) {
          qtyPromise = fetchCart().then(function (cart) {
            lastKnownCart = cart;
            var ex = cart.items.find(function (it) { return it.key === currentLineKey; });
            return ex ? ex.quantity : (itemData.quantity || 1);
          }).catch(function () { return itemData.quantity || 1; });
        } else {
          qtyPromise = Promise.resolve(itemData.quantity || 1);
        }

        // Same race as Add-to-Cart: a quick-checkout click after picking a size
        // must not navigate before the variant swap commits server-side.
        // Expose the in-flight write via pendingMutation so the checkout gate
        // can await it.
        var p = qtyPromise.then(function (serverQty) {
          var targetQty = serverQty;
          var wasClamped = false;
          if (maxQty && serverQty > maxQty) {
            targetQty = maxQty;
            wasClamped = true;
          }
          var changePromise = currentLineKey
            ? changeCartItemVariant(currentLineKey, targetQty, selectedVariantId)
            : addToCart(selectedVariantId, targetQty, null, { sections: CART_RESPONSE_SECTIONS });
          return changePromise.then(function (response) {
            return { response: response, wasClamped: wasClamped };
          });
        });
        pendingMutation = p;

        p.then(function (result) {
          var response = result.response;
          if (response && response.items && response.items.length > 0) {
            currentLineKey = response.items[0].key;
            itemData.key = currentLineKey;
            // Re-tag the STATE 1 cart-item with the new line key so the
            // notice helper finds it.
            var stateOneItem = addedItemContainer.querySelector('[data-cart-item]');
            if (stateOneItem) stateOneItem.setAttribute('data-line-key', currentLineKey);
          }
          if (response && response.sections) {
            applyCartSections(response.sections);
          }
          if (result.wasClamped && currentLineKey) {
            showCartItemNotice(currentLineKey, 'this is all we have currently');
          }
          sizeSelect.disabled = false;
          // Best-effort refresh of lastKnownCart for any non-checkout reader.
          // Intentionally NOT chained into pendingMutation — validateCheckout
          // reads STATE 2's DOM (which applyCartSections already updated above)
          // as its source of truth, so holding the gate for this fetch would
          // only cause spurious "nothing happens" delays on rapid clicks.
          fetchCart().then(function (cart) { lastKnownCart = cart; }).catch(function () {});
        }).catch(function (err) {
          console.error('Size change failed:', err);
          sizeSelect.disabled = false;
        }).then(function () {
          if (pendingMutation === p) pendingMutation = null;
        });
      });
    }
  }

  /* ============================================
     EVENT: QUANTITY CHANGE
     ============================================ */

  var qtyUpdateTimer = null;

  function handleQtyChange(e) {
    var btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    if (btn.disabled) return;

    var delta = parseInt(btn.getAttribute('data-qty-change'), 10);
    var lineKey = btn.getAttribute('data-line-key');
    var wrap = btn.closest('[data-qty-wrap]');
    if (!wrap) return;

    var input = wrap.querySelector('.cart-item__qty-val');
    if (!input) return;

    var current = parseInt(input.value, 10) || 1;
    var newQty = current + delta;
    if (newQty < 1) newQty = 1;

    // Server-rendered max-qty cap (== count of available variants for sized,
    // multi-variant products). The "+" button is also pre-disabled at the cap
    // by the Liquid render, but we re-clamp here in case the user holds the
    // key down between renders.
    var maxQty = parseInt(wrap.getAttribute('data-max-qty'), 10);
    if (maxQty && newQty > maxQty) newQty = maxQty;
    if (newQty === current) return;

    input.value = newQty;

    var plusBtn = wrap.querySelector('[data-qty-change="1"]');
    if (plusBtn && maxQty) {
      plusBtn.disabled = newQty >= maxQty;
      if (plusBtn.disabled) plusBtn.setAttribute('aria-disabled', 'true');
      else plusBtn.removeAttribute('aria-disabled');
    }

    if (lineKey) {
      clearTimeout(qtyUpdateTimer);
      cartLoading.classList.add('is-visible');
      qtyUpdateTimer = setTimeout(function() {
        var p = changeCartItem(lineKey, newQty, { sections: CART_RESPONSE_SECTIONS });
        pendingMutation = p;
        p.then(function (cart) {
          // /cart/change.js returns the full cart JSON at the top level;
          // sections (when requested) ride along under `sections`.
          if (cart) {
            lastKnownCart = cart;
            if (cart.sections) applyCartSections(cart.sections);
          }
          cartLoading.classList.remove('is-visible');
        }).catch(function(err) {
          console.error('Failed to update quantity', err);
          cartLoading.classList.remove('is-visible');
          input.value = current;
        }).then(function () {
          if (pendingMutation === p) pendingMutation = null;
        });
      }, 400); // 400ms debounce
    }
  }

  /* ============================================
     EVENT: REMOVE ITEM
     ============================================ */

  function handleRemoveClick(e) {
    var btn = e.target.closest('[data-remove-item]');
    if (!btn) return;

    var key = btn.getAttribute('data-line-key');
    var title = btn.getAttribute('data-product-title');
    var handle = btn.getAttribute('data-product-handle');

    openRemoveModal({
      key: key,
      title: title,
      handle: handle,
      source: 'cart'
    });
  }

  function handleWishlistRemoveClick(e) {
    var btn = e.target.closest('[data-wishlist-remove]');
    if (!btn) return;

    var handle = btn.getAttribute('data-wishlist-remove');
    openRemoveModal({
      key: null,
      title: '',
      handle: handle,
      source: 'wishlist'
    });
  }

  /* ============================================
     EVENT: CONFIRM REMOVE / MOVE
     ============================================ */

  function attachModalButtonListeners() {
    var confirmRemoveBtn = document.getElementById('confirm-remove-btn');
    var confirmMoveBtn = document.getElementById('confirm-move-btn');
    var confirmYesBtn = document.getElementById('confirm-yes-btn');
    var confirmNoBtn = document.getElementById('confirm-no-btn');

    // Cart: Remove button
    if (confirmRemoveBtn) {
      confirmRemoveBtn.addEventListener('click', function () {
        if (!pendingRemove || !pendingRemove.key) return;
        
        changeCartItem(pendingRemove.key, 0).then(function () {
          closeRemoveModal();
          refreshCart();
          if (currentState === 'added') {
            switchState('cart');
            updatePillToggle('cart');
          }
        });
      });
    }

    // Cart: Move to wishlist button
    if (confirmMoveBtn) {
      confirmMoveBtn.addEventListener('click', function () {
        if (!pendingRemove) return;
        
        // Get product data from cart item
        var cartItem = document.querySelector('[data-cart-item][data-line-key="' + pendingRemove.key + '"]');
        if (!cartItem) return;
        
        var image = cartItem.querySelector('.cart-item__image');
        var nameEl = cartItem.querySelector('.cart-item__name');
        var priceEl = cartItem.querySelector('.cart-item__price');
        var labelEl = cartItem.querySelector('.cart-item__craft-label-text');
        
        // Add to wishlist
        addToWishlist({
          handle: pendingRemove.handle,
          title: pendingRemove.title || (nameEl ? nameEl.textContent.trim() : ''),
          description: '',
          price: 0,
          priceFormatted: priceEl ? priceEl.textContent.trim() : '',
          image: image ? image.src : '',
          url: '/products/' + pendingRemove.handle,
          label: labelEl ? labelEl.textContent.trim() : ''
        });
        
        // Remove from cart
        changeCartItem(pendingRemove.key, 0).then(function () {
          closeRemoveModal();
          refreshCart();
          
          // Show success feedback (optional)
          console.log('Moved to wishlist');
        });
      });
    }

    // Wishlist: Yes button (confirm removal)
    if (confirmYesBtn) {
      confirmYesBtn.addEventListener('click', function () {
        if (!pendingRemove || !pendingRemove.handle) return;
        
        removeFromWishlist(pendingRemove.handle);
        closeRemoveModal();
        renderWishlistItems();
      });
    }

    // Wishlist: No button (cancel)
    if (confirmNoBtn) {
      confirmNoBtn.addEventListener('click', function () {
        closeRemoveModal();
      });
    }
  }

  // Initial attachment
  attachModalButtonListeners();

  /* ============================================
     EVENT: COUPON GRAB
     ============================================ */

  if (couponGrabBtn) {
    couponGrabBtn.addEventListener('click', function () {
      var code = couponCodeValue ? couponCodeValue.textContent.trim() : '';
      if (!code) return;

      var originalHtml = couponGrabBtn.innerHTML;
      navigator.clipboard.writeText(code).then(function () {
        couponGrabBtn.textContent = 'Copied!';
        setTimeout(function () {
          couponGrabBtn.innerHTML = originalHtml;
        }, 2000);
      }).catch(function () {
        var range = document.createRange();
        range.selectNodeContents(couponCodeValue);
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      });
    });
  }

  /* ============================================
     EVENT: VIEW CART BUTTON
     ============================================ */

  if (viewCartBtn) {
    viewCartBtn.addEventListener('click', function () {
      switchState('cart');
      refreshCart().then(function (cart) {
        if (cart && cart.items && cart.items.length > 0) {
          showCartRecommendations();
        }
      });
    });
  }

  /* ============================================
     EVENT: WISHLIST TOGGLE (on rec cards & product cards)
     ============================================ */

  function handleWishlistToggle(e) {
    var btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    var handle = btn.getAttribute('data-wishlist-toggle');
    if (!handle) return;

    if (isInWishlist(handle)) {
      removeFromWishlist(handle);
      btn.classList.remove('is-wished');
    } else {
      var card = btn.closest('[data-rec-card]') || btn.closest('.product-card');
      var title = '';
      var price = 0;
      var priceFormatted = '';
      var image = '';
      var description = '';
      var label = '';

      if (card) {
        var nameEl = card.querySelector('.rec-card__name') || card.querySelector('.product-card__title');
        var priceEl = card.querySelector('.rec-card__price') || card.querySelector('.product-card__price');
        var imgEl = card.querySelector('.rec-card__image') || card.querySelector('.product-card__image');
        var descEl = card.querySelector('.rec-card__desc');
        var labelEl = card.querySelector('.rec-card__craft-label-text') || card.querySelector('.product-card__craft-label-text');
        
        title = nameEl ? nameEl.textContent.trim() : '';
        priceFormatted = priceEl ? priceEl.textContent.trim() : '';
        image = imgEl ? imgEl.src : '';
        description = descEl ? descEl.textContent.trim() : '';
        label = labelEl ? labelEl.textContent.trim() : '';
      }

      addToWishlist({
        handle: handle,
        title: title,
        description: description,
        price: 0,
        priceFormatted: priceFormatted,
        image: image,
        url: '/products/' + handle,
        label: label
      });

      btn.classList.add('is-wished');
      
      // Open wishlist drawer
      openDrawer('wishlist');
      updatePillToggle('wishlist');
      renderWishlistItems();
    }
  }

  /* ============================================
     EVENT: WISHLIST SIZE CHANGE
     ============================================ */

  function handleWishlistSizeChange(e) {
    var select = e.target.closest('[data-wishlist-size-select]');
    if (!select) return;

    var handle = select.getAttribute('data-product-handle');
    var variantId = parseInt(select.value, 10);

    if (!variantId) {
      select.classList.add('is-invalid');
      var errorMsg = select.parentElement.querySelector('.wishlist-item__size-error');
      if (errorMsg) errorMsg.style.display = '';
      shakeSizeComponent(select.closest('.wishlist-item__size-selector') || select);
      return;
    }

    select.classList.remove('is-invalid');
    var errorMsg = select.parentElement.querySelector('.wishlist-item__size-error');
    if (errorMsg) errorMsg.style.display = 'none';

    updateWishlistVariant(handle, variantId);
  }

  /* ============================================
     EVENT: MOVE TO CART
     ============================================ */

  function handleMoveToCart(e) {
    var btn = e.target.closest('[data-wishlist-move-to-cart]');
    if (!btn) return;

    var handle = btn.getAttribute('data-product-handle');
    var item = getWishlist().find(function (i) { return i.handle === handle; });
    if (!item) return;

    var select = wishlistItemsList.querySelector('[data-wishlist-size-select][data-product-handle="' + handle + '"]');
    var variantId = null;

    // Check if size selector exists and is visible
    var sizeSelector = wishlistItemsList.querySelector('[data-wishlist-item][data-product-handle="' + handle + '"] .wishlist-item__size-selector');
    var isSelectorVisible = sizeSelector && sizeSelector.style.display !== 'none';

    if (isSelectorVisible && select) {
      // Size selector is visible - require selection
      variantId = parseInt(select.value, 10);
      if (!variantId) {
        select.classList.add('is-invalid');
        var errorMsg = select.parentElement.querySelector('.wishlist-item__size-error');
        if (errorMsg) errorMsg.style.display = '';
        shakeSizeComponent(sizeSelector || select);
        return;
      }
      select.classList.remove('is-invalid');
      var errorMsg = select.parentElement.querySelector('.wishlist-item__size-error');
      if (errorMsg) errorMsg.style.display = 'none';
    } else {
      // Size selector is hidden - use auto-selected variant from wishlist item
      variantId = item.selectedVariantId;
      if (!variantId) {
        console.error('No variant ID found for product without size selector:', handle);
        return;
      }
    }

    btn.disabled = true;
    btn.textContent = 'Adding...';

    addToCart(variantId, 1).then(function () {
      removeFromWishlist(handle);
      renderWishlistItems();
      
      // Switch to cart view
      switchState('cart');
      updatePillToggle('cart');
      return refreshCart();
    }).then(function () {
      if (lastAddedProductData && lastAddedProductData.recommendations) {
        renderRecommendations(lastAddedProductData.recommendations);
      }
    }).catch(function (err) {
      console.error('Move to cart failed:', err);
      btn.disabled = false;
      btn.textContent = 'Move to Cart';
    });
  }

  /* ============================================
     EVENT: SWITCH BETWEEN CART ↔ WISHLIST
     ============================================ */

  function updatePillToggle(activeView) {
    document.querySelectorAll('.cart-drawer__pill-btn').forEach(function (btn) {
      var view = btn.getAttribute('data-view') || 
                 (btn.hasAttribute('data-switch-wishlist') ? 'wishlist' : 'cart');
      
      if (view === activeView) {
        btn.classList.add('is-active');
      } else {
        btn.classList.remove('is-active');
      }
    });
  }

  document.querySelectorAll('[data-switch-wishlist]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      switchState('wishlist');
      updatePillToggle('wishlist');
      renderWishlistItems();
    });
  });

  document.querySelectorAll('[data-switch-cart]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      switchState('cart');
      updatePillToggle('cart');
      refreshCart().then(function (cart) {
        if (cart && cart.items && cart.items.length > 0) {
          showCartRecommendations();
        }
      });
    });
  });

  /* ============================================
     EVENT: OPEN WISHLIST FROM HEADER
     ============================================ */

  var headerWishlistBtn = document.querySelector('[data-open-wishlist]');
  if (headerWishlistBtn) {
    headerWishlistBtn.addEventListener('click', function (e) {
      e.preventDefault();
      openDrawer('wishlist');
      updatePillToggle('wishlist');
      renderWishlistItems();
    });
  }

  /* ============================================
     EVENT: CLOSE DRAWER
     ============================================ */

  document.querySelectorAll('[data-drawer-close]').forEach(function (btn) {
    btn.addEventListener('click', closeDrawer);
  });

  if (overlay) {
    overlay.addEventListener('click', closeDrawer);
  }

  if (drawerModalOverlay) {
    drawerModalOverlay.addEventListener('click', function (e) {
      if (e.target === drawerModalOverlay) closeRemoveModal();
    });
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      if (drawerModalOverlay && drawerModalOverlay.classList.contains('is-open')) {
        closeRemoveModal();
      } else if (drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    }
  });

  /* ============================================
     EVENT DELEGATION (drawer body clicks)
     ============================================ */

  drawer.addEventListener('click', function (e) {
    if (e.target.closest('[data-qty-change]')) {
      handleQtyChange(e);
      return;
    }

    if (e.target.closest('[data-remove-item]')) {
      handleRemoveClick(e);
      return;
    }

    if (e.target.closest('[data-wishlist-toggle]')) {
      handleWishlistToggle(e);
      return;
    }

    if (e.target.closest('[data-wishlist-remove]')) {
      handleWishlistRemoveClick(e);
      return;
    }

    if (e.target.closest('[data-wishlist-move-to-cart]')) {
      handleMoveToCart(e);
      return;
    }
  });

  function handleSizeChange(e) {
    var select = e.target.closest('.cart-item__size-select');
    if (!select) return;

    var lineKey = select.getAttribute('data-line-key');
    var currentQty = parseInt(select.getAttribute('data-current-qty') || '1', 10);
    var variantId = parseInt(select.value, 10);

    if (!variantId) {
      select.classList.add('is-invalid');
      var errorMsg = select.parentElement.querySelector('.cart-item__size-error');
      if (errorMsg) errorMsg.style.display = '';
      shakeSizeComponent(select.closest('.cart-item__size-selector') || select);
      return;
    }

    select.classList.remove('is-invalid');
    var errorMsg = select.parentElement.querySelector('.cart-item__size-error');
    if (errorMsg) errorMsg.style.display = 'none';

    if (lineKey && variantId) {
      // Clamp qty at the count of currently-available variants for this
      // product. The data-max-qty attribute is emitted server-side by
      // snippets/cart-item.liquid for sized, multi-variant products. If the
      // user mashed Add-to-Cart on a pending-size line and pushed qty past
      // the cap, picking a size now reduces it back down.
      var cartItem = select.closest('.cart-item');
      var qtyWrap = cartItem && cartItem.querySelector('[data-qty-wrap]');
      var maxQty = qtyWrap ? parseInt(qtyWrap.getAttribute('data-max-qty'), 10) : 0;
      var targetQty = currentQty;
      var wasClamped = false;
      if (maxQty && currentQty > maxQty) {
        targetQty = maxQty;
        wasClamped = true;
      }

      // Disable the select to prevent multiple changes
      select.disabled = true;
      cartLoading.classList.add('is-visible');

      var p = changeCartItemVariant(lineKey, targetQty, variantId);
      pendingMutation = p;
      p.then(function (response) {
        // changeCartItemVariant pipes sections through the final addToCart, so
        // response is the add.js payload: { items, sections }.
        if (response && response.sections) {
          applyCartSections(response.sections);
        }
        if (wasClamped && response && response.items && response.items[0]) {
          showCartItemNotice(response.items[0].key, 'this is all we have currently');
        }
        cartLoading.classList.remove('is-visible');
        // Best-effort refresh; not chained into pendingMutation (see the
        // matching note in the lookbook size-pick handler above).
        fetchCart().then(function (cart) { lastKnownCart = cart; }).catch(function () {});
      }).catch(function(err) {
         console.error('Failed to change size:', err);
         cartLoading.classList.remove('is-visible');
         select.disabled = false;
      }).then(function () {
        if (pendingMutation === p) pendingMutation = null;
      });
    }
  }

  function validateCheckout(e) {
    // STATE 2's cart-items HTML is rebuilt from the server-rendered
    // ajax-cart-items section after every mutation, so it's the canonical
    // view of the server cart. Any line with `_is_unselected` renders a
    // <select> with value="" — scan exclusively for that and ignore the
    // active state's UI (e.g. STATE 1's #lookbook-size-select, which is a
    // disposable picker, not a cart row).
    var cartState = document.getElementById('drawer-state-cart');
    if (!cartState) return;

    var firstInvalid = null;
    cartState.querySelectorAll('.cart-item__size-select').forEach(function (select) {
      var errorMsg = select.parentElement && select.parentElement.querySelector('.cart-item__size-error');
      if (!select.value) {
        select.classList.add('is-invalid');
        if (errorMsg) errorMsg.style.display = '';
        if (!firstInvalid) firstInvalid = select;
      } else {
        select.classList.remove('is-invalid');
        if (errorMsg) errorMsg.style.display = 'none';
      }
    });

    if (!firstInvalid) return;

    // Stop the event before Razorpay magic checkout (or any other listener) can act.
    e.preventDefault();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    e.stopPropagation();

    // Make sure the invalid line is visible — switch to STATE 2 and scroll.
    if (!cartState.classList.contains('is-active')) {
      document.querySelectorAll('.cart-drawer__state.is-active').forEach(function (s) {
        s.classList.remove('is-active');
      });
      cartState.classList.add('is-active');
    }

    firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
    shakeSizeComponent(firstInvalid.closest('.cart-item__size-selector') || firstInvalid);

    return false;
  }

  // Razorpay magic checkout attaches its own document-level capture listener,
  // so we listen at the document in capture phase too — and register early — to
  // ensure our validation runs first and can stop propagation before Razorpay
  // sees the event.
  //
  // NOTE: this gate is intentionally scoped to the in-drawer checkout buttons
  // ONLY. Do NOT add `.rzp-custom-buy-now` here — Buy Now is handled by its
  // own snapshot/restore flow and switching the drawer to STATE 2 from there
  // would be nonsensical.
  function isCheckoutTrigger(target) {
    if (!target || !target.closest) return false;
    return !!target.closest('#quick-checkout-btn, #cart-checkout-btn');
  }

  // When a checkout interaction lands while a cart mutation is in flight, we
  // can't let it through (the request would see a pre-mutation cart). Block
  // the event and let the user retry — do NOT programmatically replay
  // .click() once the gate clears: a click dispatched from a Promise.then
  // microtask runs without transient user activation, which forces
  // Razorpay's Magic Checkout to fall back to opening in a new tab instead
  // of redirecting the current tab. The gate window is short (~one
  // /cart/change.js round-trip), so a single retry click is the right
  // tradeoff against losing same-tab handoff.
  function gateCheckoutEvent(e) {
    if (!pendingMutation) return false;
    e.preventDefault();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    e.stopPropagation();
    return true;
  }

  document.addEventListener('click', function (e) {
    if (!isCheckoutTrigger(e.target)) return;
    if (gateCheckoutEvent(e)) return;
    validateCheckout(e);
  }, true);

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.querySelector) return;
    if (!form.querySelector('#quick-checkout-btn, #cart-checkout-btn')) return;
    if (gateCheckoutEvent(e)) return;
    validateCheckout(e);
  }, true);

  drawer.addEventListener('change', function(e) {
    if (e.target.classList.contains('cart-item__size-select')) {
      handleSizeChange(e);
    }
    if (e.target.classList.contains('wishlist-item__size-select')) {
      handleWishlistSizeChange(e);
    }
  });

  /* ============================================
     GLOBAL: PRODUCT CARD WISHLIST BUTTONS
     ============================================ */

  document.addEventListener('click', function (e) {
    var wishlistBtn = e.target.closest('.product-card__wishlist');
    if (wishlistBtn) {
      e.preventDefault();
      e.stopPropagation();
      
      var card = wishlistBtn.closest('.product-card');
      if (!card) return;

      var link = card.querySelector('.product-card__link');
      if (!link) return;

      var href = link.getAttribute('href');
      var handle = href ? href.split('/products/')[1] : '';
      if (!handle) return;

      if (isInWishlist(handle)) {
        removeFromWishlist(handle);
        wishlistBtn.classList.remove('is-wished');
      } else {
        var title = card.querySelector('.product-card__title');
        var price = card.querySelector('.product-card__price');
        var image = card.querySelector('.product-card__image');
        var label = card.querySelector('.product-card__craft-label-text');

        addToWishlist({
          handle: handle,
          title: title ? title.textContent.trim() : '',
          description: '',
          price: 0,
          priceFormatted: price ? price.textContent.trim() : '',
          image: image ? image.src : '',
          url: href,
          label: label ? label.textContent.trim() : ''
        });

        wishlistBtn.classList.add('is-wished');
        
        // Open wishlist drawer
        openDrawer('wishlist');
        updatePillToggle('wishlist');
        renderWishlistItems();
      }
    }
  });

  /* ============================================
     INTERCEPT ADD TO CART FORM
     ============================================ */

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (form.matches('[data-product-form]') || form.id === 'product-form') {
      var submitter = e.submitter;
      if (submitter && submitter.classList.contains('product-info__add-to-cart')) {
        handleAddToCart(e);
      }
    }
  });

  // The document.addEventListener('click', ...) block for the add-to-cart button has been removed completely to prevent double-firing.

  /* ============================================
     OPEN CART FROM HEADER ICON
     ============================================ */

  var headerCartLink = document.querySelector('.header__icon-button--cart');
  if (headerCartLink) {
    headerCartLink.addEventListener('click', function (e) {
      e.preventDefault();
      openDrawer('cart');
      updatePillToggle('cart');
      refreshCart().then(function (cart) {
        if (cart && cart.items && cart.items.length > 0) {
          showCartRecommendations();
        }
      });
    });
  }

  /* ============================================
     UTILITIES
     ============================================ */

  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function stripHtml(html) {
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  function truncateText(str, maxLen) {
    if (!str) return '';
    if (str.length <= maxLen) return str;
    return str.substring(0, maxLen) + '…';
  }

  function getSizedImageUrl(url, size) {
    if (!url) return '';
    if (url.indexOf('cdn.shopify.com') > -1) {
      return url.replace(/(\.[^.]+)$/, '_' + size + '$1');
    }
    return url;
  }

  /* ============================================
     INIT: Update wishlist count on load
     ============================================ */

  updateWishlistCount();

  // Prime lastKnownCart from the cart cookie so the source-of-truth check in
  // validateCheckout has something to consult before the user opens STATE 2
  // or performs any mutation. Best-effort; failure is silent.
  fetchCart().then(function (cart) { lastKnownCart = cart; }).catch(function () {});

  // Expose wishlist functions globally for other scripts
  window.isInWishlist = isInWishlist;
  window.addToWishlist = addToWishlist;
  window.removeFromWishlist = removeFromWishlist;
  window.updateWishlistCount = updateWishlistCount;
  window.openWishlistDrawer = function() {
    openDrawer('wishlist');
    updatePillToggle('wishlist');
    renderWishlistItems();
  };

  // Update wishlist button states on product cards
  document.querySelectorAll('.product-card').forEach(function (card) {
    var link = card.querySelector('.product-card__link');
    if (!link) return;

    var href = link.getAttribute('href');
    var handle = href ? href.split('/products/')[1] : '';
    if (!handle) return;

    if (isInWishlist(handle)) {
      var btn = card.querySelector('.product-card__wishlist');
      if (btn) btn.classList.add('is-wished');
    }
  });

  /* ============================================
     OPEN CART FROM A LINK
     - /cart redirects to /#cart (sections/cart.liquid).
     - WhatsApp cart reminders link to /?restore_cart=VARIANT:QTY,VARIANT:QTY
       so the cart comes back in any browser (e.g. after shopping inside
       Instagram's). Items already in the cart aren't added twice.
     ============================================ */

  function parseRestoreCart(value) {
    var wanted = {};
    value.split(',').forEach(function (part) {
      var m = /^(\d{1,20}):(\d{1,2})$/.exec(part.trim());
      if (m && Number(m[2]) > 0) wanted[m[1]] = (wanted[m[1]] || 0) + Number(m[2]);
    });
    return wanted;
  }

  function restoreCart(wanted) {
    return botCheckWarmUp
      .then(function () {
        return fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
      })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var have = {};
        (cart.items || []).forEach(function (item) {
          have[item.variant_id] = (have[item.variant_id] || 0) + item.quantity;
        });
        // One request per item so a sold-out variant doesn't block the rest.
        return Object.keys(wanted).reduce(function (chain, id) {
          var missing = wanted[id] - (have[id] || 0);
          if (missing <= 0) return chain;
          return chain.then(function () {
            return fetch('/cart/add.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ items: [{ id: Number(id), quantity: missing }] })
            }).catch(function () {});
          });
        }, Promise.resolve());
      })
      .catch(function () {});
  }

  function openCartFromLink() {
    openDrawer('cart');
    updatePillToggle('cart');
    refreshCart().then(function (cart) {
      if (cart && cart.items && cart.items.length > 0) {
        showCartRecommendations();
      }
    });
  }

  var linkParams = new URLSearchParams(window.location.search);
  var restoreParam = linkParams.get('restore_cart');
  if (drawer && (restoreParam !== null || window.location.hash === '#cart')) {
    linkParams.delete('restore_cart');
    var cleanQuery = linkParams.toString();
    history.replaceState(null, '', window.location.pathname + (cleanQuery ? '?' + cleanQuery : ''));
    var wanted = restoreParam ? parseRestoreCart(restoreParam) : {};
    (Object.keys(wanted).length ? restoreCart(wanted) : Promise.resolve()).then(openCartFromLink);
  }

})();
